"use client"

import type React from "react"
import { useState, useEffect, useContext, useMemo } from "react"
import { AuthContext } from "../../Auth/context/AuthContext" // Real AuthContext
import { X, Plus, Trash2, Loader2 } from "lucide-react"
import axios from 'axios'
import type { Contact, Company, Office } from "../types"
import useContactTypes from "../hooks/useContactTypes"
//import { mockCompanies, mockOffices } from "../mockData" // Removed mock data

interface ContactFormProps {
  contact: Contact | null
  onClose: () => void
  /** Called with the saved contact after creation/update */
  onSubmit: (contact: Contact) => void
  /** Optional initial contact kind ('Business' or 'Individual') for new contacts */
  initialContactKind?: string
  /** Optional initial contact sub-type (contactType) for new contacts */
  initialContactType?: string
}

export default function ContactForm({ contact, onClose, onSubmit, initialContactKind, initialContactType }: ContactFormProps) {
  const { auth } = useContext(AuthContext)
  const contactTypes = useContactTypes()
  // Exclude 'Individual' from sub-type options, memoized to avoid identity changes
  const filteredContactTypes = useMemo(
    () => contactTypes.filter((t) => t !== 'Individual'),
    [contactTypes]
  )
  const [formData, setFormData] = useState({
    contactKind: "Individual",
    contactType: "",
    name: "",
    email: "",
    phone: "",
    companyId: "",
    companyName: "",
    officeId: "",
    officeName: "",
    address: "",
    city: "",
    stateProvince: "",
    country: "",
    isHQ: false,
  })

  const [companies, setCompanies] = useState<Company[]>([])
  const [offices, setOffices] = useState<Office[]>([])
  const [customFields, setCustomFields] = useState<{ key: string; value: string }[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [creatingNewCompany, setCreatingNewCompany] = useState(false)
  const [creatingNewOffice, setCreatingNewOffice] = useState(false)

  // Fetch companies on mount
  useEffect(() => {
    const fetchCompanies = async () => {
      if (!auth?.token) return;
      try {
        const resp = await axios.get(
          "/api/myteconstruction/companies",
          { headers: { Authorization: `Bearer ${auth.token}` } }
        );
        setCompanies(resp.data.data);
      } catch (err) {
        console.error("Failed to load companies:", err);
      }
    };
    fetchCompanies();
  }, [auth?.token]);

  // Fetch offices when company is selected
  useEffect(() => {
    const fetchOffices = async () => {
      if (!auth?.token || !formData.companyId) {
        setOffices([]);
        return;
      }
      try {
        const resp = await axios.get(
          `/api/myteconstruction/companies/${formData.companyId}/offices`,
          { headers: { Authorization: `Bearer ${auth.token}` } }
        );
        setOffices(resp.data.data);
      } catch (err) {
        console.error("Failed to load offices:", err);
      }
    };
    fetchOffices();
  }, [auth?.token, formData.companyId]);

  // Initialize form with contact data if editing
  useEffect(() => {
    if (contact) {
      setFormData({
        contactKind: contact.contactKind,
        contactType: contact.contactType,
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
        companyId: contact.companyId?._id || "",
        companyName: contact.companyId?.companyName || "",
        officeId: contact.officeId?._id || "",
        officeName: contact.officeId?.officeName || "",
        address: contact.officeId?.address || "",
        city: contact.officeId?.city || "",
        stateProvince: contact.officeId?.stateProvince || "",
        country: contact.officeId?.country || "",
        isHQ: false,
      })

      // Initialize custom fields
      if (contact.custom) {
        const customEntries = Object.entries(contact.custom).map(([key, value]) => ({
          key,
          value: String(value),
        }))
        setCustomFields(customEntries)
      }
    } else {
      // Default values for new contact, allow overrides via initial props
      setFormData({
        contactKind: initialContactKind ?? "Individual",
        contactType: initialContactType ?? (filteredContactTypes.length > 0 ? filteredContactTypes[0] : ""),
        name: "",
        email: "",
        phone: "",
        companyId: "",
        companyName: "",
        officeId: "",
        officeName: "",
        address: "",
        city: "",
        stateProvince: "",
        country: "",
        isHQ: false,
      })
      setCustomFields([])
    }
  }, [contact, contactTypes, filteredContactTypes, initialContactKind, initialContactType])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target

    // Handle checkbox
    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked
      setFormData((prev) => ({ ...prev, [name]: checked }))
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }))
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const handleCustomFieldChange = (index: number, field: "key" | "value", value: string) => {
    setCustomFields((prev) => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: value }
      return updated
    })
  }

  const addCustomField = () => {
    setCustomFields((prev) => [...prev, { key: "", value: "" }])
  }

  const removeCustomField = (index: number) => {
    setCustomFields((prev) => prev.filter((_, i) => i !== index))
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = "Name is required"
    }

    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format"
    }

    if (!formData.contactType) {
      newErrors.contactType = "Contact sub-type is required"
    }

    if (formData.contactKind === "Business") {
      if (!formData.companyId && !formData.companyName.trim()) {
        newErrors.companyName = "Company information is required for Business contacts"
      }

      // Business contacts: validate office fields when creating new or entering office details
      if (creatingNewOffice || (!formData.officeId && formData.officeName.trim())) {
        if (!formData.officeName.trim())      newErrors.officeName    = "Office name is required";
        if (!formData.address.trim())         newErrors.address       = "Address is required";
        if (!formData.city.trim())            newErrors.city          = "City is required";
        if (!formData.stateProvince.trim())   newErrors.stateProvince = "State/Province is required";
        if (!formData.country.trim())         newErrors.country       = "Country is required";
      }
    }

    // Validate custom fields
    const customKeys = new Set<string>()
    customFields.forEach((field, index) => {
      if (field.key.trim() && customKeys.has(field.key.trim())) {
        newErrors[`customKey${index}`] = "Duplicate field name"
      }
      customKeys.add(field.key.trim())
    })

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm() || !auth?.token) return

    setLoading(true)

    try {
      // Convert custom fields to object
      const custom = customFields.reduce(
        (acc, { key, value }) => {
          if (key.trim()) {
            acc[key.trim()] = value
          }
          return acc
        },
        {} as Record<string, string>,
      )

      // Real API call to save or update contact
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const payload: any = {
        contactKind: formData.contactKind,
        contactType: formData.contactType,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        custom
      };
      if (creatingNewCompany && formData.companyName.trim()) {
        payload.companyName = formData.companyName.trim();
      } else if (formData.companyId) {
        payload.companyId = formData.companyId;
      }
      // if new company → force new office
      const isNewOffice = creatingNewOffice || creatingNewCompany;
      if (isNewOffice && formData.officeName.trim()) {
        payload.officeName    = formData.officeName.trim();
        payload.address       = formData.address.trim();
        payload.city          = formData.city.trim();
        payload.stateProvince = formData.stateProvince.trim();
        payload.country       = formData.country.trim();
        payload.isHQ          = formData.isHQ;
      } else if (formData.officeId) {
        payload.officeId = formData.officeId;
      }
      try {
        const url = contact?._id
          ? `/api/myteconstruction/contacts/${contact._id}`
          : `/api/myteconstruction/contacts`;
        const method = contact?._id ? 'put' : 'post';
        const resp = await axios[method](url, payload, {
          headers: { Authorization: `Bearer ${auth?.token}` }
        });
        // Notify caller with the saved contact record
        const saved: Contact = resp.data.data;
        onSubmit(saved);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        const msg = err?.response?.data?.message || 'Failed to save contact.';
        alert(msg);
        return;
      } finally {
        setLoading(false);
      }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      console.error("Error saving contact:", err)
      setLoading(false)
    }
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-white">{contact ? "Edit Contact" : "New Contact"}</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors p-1 rounded-full hover:bg-gray-800"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-gray-800 p-4 rounded-lg">
          <h3 className="text-md font-medium text-gray-300 mb-3">Basic Information</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Contact Type</label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="contactKind"
                    value="Individual"
                    checked={formData.contactKind === "Individual"}
                  onChange={(e) => {
                    handleInputChange(e);
                    setFormData((prev) => ({
                      ...prev,
                      contactType: prev.contactType || (filteredContactTypes[0] || "")
                    }));
                  }}
                    className="mr-2"
                  />
                  <span>Individual</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="contactKind"
                    value="Business"
                    checked={formData.contactKind === "Business"}
                    onChange={(e) => {
                      handleInputChange(e);
                      setFormData((prev) => ({
                        ...prev,
                        contactType: prev.contactType || (filteredContactTypes[0] || "")
                      }));
                    }}
                    className="mr-2"
                  />
                  <span>Business</span>
                </label>
              </div>
            </div>

            <div>
            <label className="block text-sm text-gray-400 mb-1">Contact Sub-Type</label>
              <select
                name="contactType"
                value={formData.contactType}
                onChange={handleInputChange}
                className={`w-full bg-gray-700 text-white rounded-md p-2 border ${
                  errors.contactType ? "border-red-500" : "border-gray-600"
                }`}
              >
                <option value="">Select a role</option>
                {filteredContactTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              {errors.contactType && <p className="text-red-500 text-xs mt-1">{errors.contactType}</p>}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm text-gray-400 mb-1">Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full bg-gray-700 text-white rounded-md p-2 border ${
                  errors.name ? "border-red-500" : "border-gray-600"
                }`}
                placeholder="Full name"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full bg-gray-700 text-white rounded-md p-2 border ${
                  errors.email ? "border-red-500" : "border-gray-600"
                }`}
                placeholder="Email address"
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full bg-gray-700 text-white rounded-md p-2 border border-gray-600"
                placeholder="Phone number"
              />
            </div>
          </div>
        </div>

        {/* Business Information (only if Business contact) */}
        {formData.contactKind === "Business" && (
          <div className="bg-gray-800 p-4 rounded-lg">
            <h3 className="text-md font-medium text-gray-300 mb-3">Business Information</h3>

            {/* Company Selection */}
            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-1">Company</label>
              {creatingNewCompany ? (
                <div>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    className={`w-full bg-gray-700 text-white rounded-md p-2 border ${
                      errors.companyName ? "border-red-500" : "border-gray-600"
                    }`}
                    placeholder="New company name"
                  />
                  {errors.companyName && <p className="text-red-500 text-xs mt-1">{errors.companyName}</p>}
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setCreatingNewCompany(false)
                        setFormData((prev) => ({ ...prev, companyName: "" }))
                      }}
                      className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded-md text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <select
                    name="companyId"
                    value={formData.companyId}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        companyId: e.target.value,
                        officeId: "", // Reset office when company changes
                      }))
                    }}
                    className={`w-full bg-gray-700 text-white rounded-md p-2 border ${
                      errors.companyId ? "border-red-500" : "border-gray-600"
                    }`}
                  >
                    <option value="">Select a company</option>
                    {companies.map((company) => (
                      <option key={company._id} value={company._id}>
                        {company.companyName}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      setCreatingNewCompany(true)
                      setFormData((prev) => ({ ...prev, companyId: "" }))
                    }}
                    className="mt-2 text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    <span>Create New Company</span>
                  </button>
                </div>
              )}
            </div>

            {/* Office Selection */}
            {(formData.companyId || creatingNewCompany) && (
              <div>
                <label className="block text-sm text-gray-400 mb-1">Office</label>
                {creatingNewOffice || !formData.companyId ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Office Name</label>
                      <input
                        type="text"
                        name="officeName"
                        value={formData.officeName}
                        onChange={handleInputChange}
                        className={`w-full bg-gray-700 text-white rounded-md p-2 border ${
                          errors.officeName ? "border-red-500" : "border-gray-600"
                        }`}
                        placeholder="Office name (e.g., Headquarters, Branch)"
                      />
                      {errors.officeName && <p className="text-red-500 text-xs mt-1">{errors.officeName}</p>}
                    </div>

                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Address</label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        className={`w-full bg-gray-700 text-white rounded-md p-2 border ${
                          errors.address ? "border-red-500" : "border-gray-600"
                        }`}
                        placeholder="Street address"
                      />
                      {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">City</label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          className={`w-full bg-gray-700 text-white rounded-md p-2 border ${
                            errors.city ? "border-red-500" : "border-gray-600"
                          }`}
                          placeholder="City"
                        />
                        {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                      </div>

                      <div>
                        <label className="block text-sm text-gray-400 mb-1">State/Province</label>
                        <input
                          type="text"
                          name="stateProvince"
                          value={formData.stateProvince}
                          onChange={handleInputChange}
                          className={`w-full bg-gray-700 text-white rounded-md p-2 border ${
                            errors.stateProvince ? "border-red-500" : "border-gray-600"
                          }`}
                          placeholder="State/Province"
                        />
                        {errors.stateProvince && <p className="text-red-500 text-xs mt-1">{errors.stateProvince}</p>}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Country</label>
                      <input
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        className={`w-full bg-gray-700 text-white rounded-md p-2 border ${
                          errors.country ? "border-red-500" : "border-gray-600"
                        }`}
                        placeholder="Country"
                      />
                      {errors.country && <p className="text-red-500 text-xs mt-1">{errors.country}</p>}
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="isHQ"
                        checked={formData.isHQ}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      <label className="text-sm text-gray-400">This is the headquarters</label>
                    </div>

                    {formData.companyId && (
                      <div className="mt-2 flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setCreatingNewOffice(false)
                            setFormData((prev) => ({
                              ...prev,
                              officeName: "",
                              address: "",
                              city: "",
                              stateProvince: "",
                              country: "",
                              isHQ: false,
                            }))
                          }}
                          className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded-md text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <select
                      name="officeId"
                      value={formData.officeId}
                      onChange={handleInputChange}
                      className={`w-full bg-gray-700 text-white rounded-md p-2 border ${
                        errors.officeId ? "border-red-500" : "border-gray-600"
                      }`}
                    >
                      <option value="">Select an office</option>
                      {offices.map((office) => (
                        <option key={office._id} value={office._id}>
                          {office.officeName} - {office.city}, {office.stateProvince}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setCreatingNewOffice(true)}
                      className="mt-2 text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      <span>Create New Office</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Custom Fields */}
        <div className="bg-gray-800 p-4 rounded-lg">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-md font-medium text-gray-300">Custom Fields</h3>
            <button
              type="button"
              onClick={addCustomField}
              className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
            >
              <Plus className="h-3.5 w-3.5" />
              <span>Add Field</span>
            </button>
          </div>

          {customFields.length > 0 ? (
            <div className="space-y-3">
              {customFields.map((field, index) => (
                <div key={index} className="flex gap-3 items-start">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={field.key}
                      onChange={(e) => handleCustomFieldChange(index, "key", e.target.value)}
                      className={`w-full bg-gray-700 text-white rounded-md p-2 border ${
                        errors[`customKey${index}`] ? "border-red-500" : "border-gray-600"
                      }`}
                      placeholder="Field name"
                    />
                    {errors[`customKey${index}`] && (
                      <p className="text-red-500 text-xs mt-1">{errors[`customKey${index}`]}</p>
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="text"
                      value={field.value}
                      onChange={(e) => handleCustomFieldChange(index, "value", e.target.value)}
                      className="w-full bg-gray-700 text-white rounded-md p-2 border border-gray-600"
                      placeholder="Value"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeCustomField(index)}
                    className="p-2 bg-red-600 hover:bg-red-700 rounded-md transition-colors"
                    title="Remove field"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No custom fields. Click "Add Field" to create one.</p>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <span>{contact ? "Update Contact" : "Create Contact"}</span>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
